/**
 * Export a Privacy incident as a modern, themed case file — Word (.docx) or PDF.
 *
 * One content model (buildCaseModel) drives both renderers so the Word and the
 * PDF are identical in substance: the risk assessment plus the rest of the story
 * — mitigation actions (the accepted ones), notifications, sanctions, and
 * education. Those four sections always appear; when a section has nothing on
 * file it reads "N/A" so the reader knows it was considered, not skipped.
 *
 * Theme (color and layout only) is borrowed from the PSI board deck: a modern
 * orange brand band over slate ink. PSI itself is untouched.
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  ShadingType
} from 'docx';
import { saveAs } from 'file-saver';
import { notificationDeadlineDisplay } from './exportPrivacyCasePdf';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ---------------------------------------------------------------------------
// Theme — house warm palette (navy ink, orange accents).
// Hex without the leading '#'. RGB triples for the PDF renderer.
// ---------------------------------------------------------------------------
const THEME = {
  brand: 'F97316',      // modern orange
  brandDark: 'EA580C',
  ink: '1E293B',
  inkMid: '475569',
  inkSoft: '64748B',
  rule: 'E2E8F0',
  panel: 'F8FAFC',
  panelAlt: 'FFF7ED',   // faint orange wash for striped rows
  white: 'FFFFFF'
};
const RGB = {
  brand: [249, 115, 22],
  brandDark: [234, 88, 12],
  ink: [30, 41, 59],
  inkMid: [71, 85, 105],
  inkSoft: [100, 116, 139],
  rule: [226, 232, 240],
  panel: [248, 250, 252],
  panelAlt: [255, 247, 237],
  white: [255, 255, 255]
};

// ---------------------------------------------------------------------------
// Value helpers — blank-safe. na() is the contract: anything empty becomes N/A.
// ---------------------------------------------------------------------------
const NA = 'N/A';
function na(v) {
  if (v == null) return NA;
  const s = String(v).trim();
  return s === '' ? NA : s;
}
function fmtDate(v) {
  if (!v) return NA;
  const s = String(v).slice(0, 10);
  return s || NA;
}
function joinMeta(parts) {
  const v = parts.filter((p) => p != null && String(p).trim() !== '').join('  ·  ');
  return v || NA;
}

// ---------------------------------------------------------------------------
// Closed-case report cleanup (reporting only — the stored assessment and the
// assessor agent are NOT modified). When the assessor runs, it bakes its
// in-flight scaffolding into the saved narrative and factor notes: "Next steps",
// "Open questions to resolve", per-factor "Still needed", "Guardrails", and a
// "(review and edit)" prompt. Those belong to an investigation in progress.
// Once the case is closed they are no longer pending, so the closed-case report
// shows only what was substantive to the determination: the analysis summary,
// the notification plan, and the authorities relied on.
//
// The assembled text separates each block with a blank line (see
// server/routes/privacy/index.js), so we split on blank lines and drop any
// block whose first line is one of the pending/process headers.
// ---------------------------------------------------------------------------
const PENDING_BLOCK_HEADERS = [
  'next steps:',
  'open questions to resolve:',
  'still needed:',
  'guardrails:'
];
function stripPendingForClosed(text) {
  if (text == null) return text;
  const blocks = String(text).split(/\n{2,}/);
  const kept = blocks.filter((block) => {
    const firstLine = block.split('\n').find((l) => l.trim() !== '') || '';
    return !PENDING_BLOCK_HEADERS.includes(firstLine.trim().toLowerCase());
  });
  return kept
    .join('\n\n')
    // The trailing attribution carries a "(review and edit)" prompt that only
    // makes sense while the assessment is still a draft. Drop it once closed.
    .replace(/\s*\(review and edit\)\s*/gi, ' ')
    .trim();
}

// ---------------------------------------------------------------------------
// Content model — single source of truth for both renderers.
// Each section is either a key/value block (rows of [label, value]) or a
// record table (columns + rows). `empty` marks sections with nothing on file.
// ---------------------------------------------------------------------------
export function buildCaseModel(incident) {
  const arr = (x) => (Array.isArray(x) ? x : []);
  const inc = incident || {};
  const id = inc.id ? String(inc.id).slice(0, 8) : '';

  const STATUS_LABELS = {
    draft: 'Draft',
    under_investigation: 'Under investigation',
    risk_assessment_complete: 'Risk assessment complete',
    determination_pending_approval: 'Pending approval',
    approved_not_breach: 'Not a breach',
    approved_breach: 'Breach',
    notification_complete: 'Notification complete',
    closed: 'Closed'
  };

  // --- Overview (context) ---
  const overview = [
    ['Incident first known', fmtDate(inc.incident_known_date || inc.discovery_date)],
    ['Breach discovered (clock start, 45 CFR 164.404(a)(2))', fmtDate(inc.breach_discovered_date)],
    ['Determination approved', fmtDate(inc.breach_determined_date)],
    ['Notification deadline', notificationDeadlineDisplay(inc)],
    ['State reporting deadline', fmtDate(inc.state_deadline_date)],
    ['Brief summary', na(inc.summary)],
    ['What happened', na(inc.facts)],
    ['Who was affected', na(inc.who_impacted)],
    ['Type of information involved', na(inc.phi_involved)],
    ['Root cause', joinMeta([inc.root_cause_category && String(inc.root_cause_category).replace(/_/g, ' '), inc.root_cause_notes])],
    ['Department / area', na(inc.department_name)],
    ['Number affected', inc.number_affected != null ? String(inc.number_affected) : NA],
    ['Business associate involved', inc.business_associate_involved ? 'Yes' : 'No']
  ];

  // --- Risk assessment ---
  // Once the case is closed, the report drops the assessor's in-flight
  // scaffolding (pending questions, next steps, guardrails) and keeps only the
  // substantive findings. `clean` is a pass-through until then.
  const isClosed = inc.status === 'closed';
  const clean = (v) => (isClosed ? stripPendingForClosed(v) : v);
  const ra = inc.current_risk_assessment || arr(inc.risk_assessments)[0] || null;
  const riskRows = ra
    ? [
        ['Final determination', na(ra.determination)],
        ['Combined score', ra.combined_score != null ? String(ra.combined_score) : NA],
        ['Likelihood (overall)', na(ra.likelihood_overall)],
        ['Impact (overall)', na(ra.impact_overall)],
        ['Exception applied', na(ra.exception_applied)],
        ['Exception notes', na(ra.exception_notes)],
        ['Factor 1 — nature & extent of PHI', joinMeta([ra.factor1_likelihood, ra.factor1_impact, ra.factor1_score != null ? `score ${ra.factor1_score}` : null, clean(ra.factor1_notes)])],
        ['Factor 2 — unauthorized person', joinMeta([ra.factor2_likelihood, ra.factor2_impact, ra.factor2_score != null ? `score ${ra.factor2_score}` : null, clean(ra.factor2_notes)])],
        ['Factor 3 — PHI acquired or viewed', joinMeta([ra.factor3_likelihood, ra.factor3_impact, ra.factor3_score != null ? `score ${ra.factor3_score}` : null, clean(ra.factor3_notes)])],
        ['Factor 4 — risk mitigated', joinMeta([ra.factor4_likelihood, ra.factor4_impact, ra.factor4_score != null ? `score ${ra.factor4_score}` : null, clean(ra.factor4_notes)])],
        ['Summary for the case file', na(clean(ra.narrative_summary))]
      ]
    : [['Risk assessment', NA]];

  // --- Mitigation actions — ACCEPTED only. incident.mitigations is exactly the
  //     accepted set; AI proposals live in a separate table until accepted. ---
  const mits = arr(inc.mitigations);
  // A mitigation that was actually taken reads Closed; Date taken already
  // carries the when. Open is reserved for one with no taken or completed
  // date at all. (Same rule as the PDF export.)
  const mitigations = {
    columns: ['Action', 'Description', 'Type', 'Date taken', 'Status'],
    rows: mits.map((m) => [
      na(m.title || m.action_type),
      na(m.description),
      na(m.action_type),
      fmtDate(m.action_date),
      (m.completed_at || m.action_date) ? 'Closed' : 'Open'
    ]),
    empty: mits.length === 0
  };

  // --- Notifications ---
  const notes = arr(inc.notifications);
  const notifications = {
    columns: ['Type', 'Sent date', 'Method', 'Notes'],
    rows: notes.map((n) => [
      na(n.notification_type && String(n.notification_type).replace(/_/g, ' ')),
      fmtDate(n.sent_date),
      na(n.method),
      na(n.notes)
    ]),
    empty: notes.length === 0
  };

  // --- Sanctions ---
  const sancs = arr(inc.sanctions);
  const sanctions = {
    columns: ['Sanction', 'Type', 'Applied date'],
    rows: sancs.map((s) => [na(s.description), na(s.sanction_type), fmtDate(s.applied_at)]),
    empty: sancs.length === 0
  };

  // --- Education / training ---
  const edus = arr(inc.education_actions);
  const education = {
    columns: ['Title', 'Description', 'Completed date'],
    rows: edus.map((e) => [na(e.title), na(e.description), fmtDate(e.completed_at)]),
    empty: edus.length === 0
  };

  return {
    title: inc.title || 'Privacy incident',
    id,
    status: STATUS_LABELS[inc.status] || inc.status || NA,
    generatedAt: new Date().toLocaleString(),
    overview,
    riskAssessment: { rows: riskRows, present: !!ra },
    mitigations,
    notifications,
    sanctions,
    education
  };
}

// ===========================================================================
// WORD (.docx) renderer
// ===========================================================================
function wText(text, opts = {}) { return new TextRun({ text: text == null ? '' : String(text), ...opts }); }

// Section heading: brand-orange label over a thick brand rule for a modern divider.
function wHeading(text) {
  return new Paragraph({
    spacing: { before: 320, after: 140 },
    border: { bottom: { color: THEME.brand, style: BorderStyle.SINGLE, size: 18, space: 6 } },
    children: [wText(text.toUpperCase(), { bold: true, color: THEME.brand, size: 26 })]
  });
}

function wCell(children, { width, fill, align } = {}) {
  return new TableCell({
    width: width ? { size: width, type: WidthType.PERCENTAGE } : undefined,
    shading: fill ? { type: ShadingType.CLEAR, fill, color: 'auto' } : undefined,
    margins: { top: 60, bottom: 60, left: 110, right: 110 },
    children: Array.isArray(children) ? children : [children],
    verticalAlign: align
  });
}

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const HAIRLINE = { style: BorderStyle.SINGLE, size: 2, color: THEME.rule };

// Key/value table (Overview, Risk assessment).
function wKeyValueTable(rows) {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: { top: HAIRLINE, bottom: HAIRLINE, left: NO_BORDER, right: NO_BORDER, insideHorizontal: HAIRLINE, insideVertical: NO_BORDER },
    rows: rows.map(([label, value], i) =>
      new TableRow({
        children: [
          wCell(new Paragraph({ children: [wText(label, { bold: true, color: THEME.inkMid, size: 19 })] }), { width: 32, fill: i % 2 ? THEME.panel : THEME.white }),
          wCell(new Paragraph({ children: [wText(na(value), { color: THEME.ink, size: 19 })] }), { width: 68, fill: i % 2 ? THEME.panel : THEME.white })
        ]
      })
    )
  });
}

// Record table (Mitigations, Notifications, Sanctions, Education) with an
// orange header row and striped body. Renders an N/A line when empty.
function wRecordTable(section) {
  if (section.empty) {
    return new Paragraph({ spacing: { after: 80 }, children: [wText(NA + ' — none recorded.', { italics: true, color: THEME.inkSoft, size: 19 })] });
  }
  const header = new TableRow({
    tableHeader: true,
    children: section.columns.map((c) =>
      wCell(new Paragraph({ children: [wText(c.toUpperCase(), { bold: true, color: THEME.white, size: 17 })] }), { fill: THEME.brand })
    )
  });
  const body = section.rows.map((r, i) =>
    new TableRow({
      children: r.map((cell) =>
        wCell(new Paragraph({ children: [wText(na(cell), { color: THEME.ink, size: 18 })] }), { fill: i % 2 ? THEME.panelAlt : THEME.white })
      )
    })
  );
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: { top: NO_BORDER, bottom: HAIRLINE, left: NO_BORDER, right: NO_BORDER, insideHorizontal: HAIRLINE, insideVertical: HAIRLINE },
    rows: [header, ...body]
  });
}

// Orange cover band.
function wCoverBand(model) {
  const band = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER, insideHorizontal: NO_BORDER, insideVertical: NO_BORDER },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            shading: { type: ShadingType.CLEAR, fill: THEME.brand, color: 'auto' },
            margins: { top: 240, bottom: 240, left: 240, right: 240 },
            children: [
              new Paragraph({ children: [wText('PRIVACY INCIDENT — CASE FILE', { bold: true, color: THEME.white, size: 20 })], spacing: { after: 60 } }),
              new Paragraph({ children: [wText(model.title, { bold: true, color: THEME.white, size: 40 })], spacing: { after: 80 } }),
              new Paragraph({
                children: [
                  wText(`Status: ${model.status}`, { color: THEME.white, size: 19 }),
                  ...(model.id ? [wText(`     Case ID: ${model.id}`, { color: THEME.white, size: 19 })] : [])
                ]
              })
            ]
          })
        ]
      })
    ]
  });
  return band;
}

function buildWordDoc(model) {
  const children = [
    wCoverBand(model),
    new Paragraph({ spacing: { before: 80, after: 240 }, children: [wText(`Generated ${model.generatedAt}`, { italics: true, color: THEME.inkSoft, size: 16 })] }),

    wHeading('Case overview'),
    wKeyValueTable(model.overview),

    wHeading('Risk assessment'),
    wKeyValueTable(model.riskAssessment.rows),

    wHeading('Mitigation actions (accepted)'),
    wRecordTable(model.mitigations),

    wHeading('Notifications'),
    wRecordTable(model.notifications),

    wHeading('Sanctions'),
    wRecordTable(model.sanctions),

    wHeading('Education & training'),
    wRecordTable(model.education)
  ];

  return new Document({
    styles: { default: { document: { run: { font: 'Calibri', color: THEME.ink } } } },
    sections: [{
      properties: { page: { margin: { top: 720, bottom: 720, left: 720, right: 720 } } },
      children
    }]
  });
}

export function exportFullCaseToWord(incident) {
  const model = buildCaseModel(incident);
  const doc = buildWordDoc(model);
  Packer.toBlob(doc).then((blob) => {
    saveAs(blob, `privacy-case-${model.id || 'incident'}-${new Date().toISOString().slice(0, 10)}.docx`);
  });
}

// ===========================================================================
// PDF renderer (jsPDF + autotable) — same model, same theme.
// ===========================================================================
const PAGE = { marginX: 48, top: 48 };

function pdfHeading(doc, text, y) {
  const W = doc.internal.pageSize.getWidth();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...RGB.brand);
  doc.text(text.toUpperCase(), PAGE.marginX, y);
  doc.setDrawColor(...RGB.brand);
  doc.setLineWidth(1.5);
  doc.line(PAGE.marginX, y + 5, W - PAGE.marginX, y + 5);
  return y + 16;
}

function pdfKeyValueTable(doc, rows, startY) {
  autoTable(doc, {
    startY,
    margin: { left: PAGE.marginX, right: PAGE.marginX },
    body: rows.map(([l, v]) => [l, na(v)]),
    theme: 'plain',
    styles: { fontSize: 9.5, cellPadding: 5, textColor: RGB.ink, lineColor: RGB.rule, lineWidth: 0.5, valign: 'top' },
    columnStyles: {
      0: { cellWidth: 150, fontStyle: 'bold', textColor: RGB.inkMid },
      1: { textColor: RGB.ink }
    },
    didParseCell: (data) => {
      if (data.row.index % 2 === 1) data.cell.styles.fillColor = RGB.panel;
      else data.cell.styles.fillColor = RGB.white;
    }
  });
  return doc.lastAutoTable.finalY;
}

function pdfRecordTable(doc, section, startY) {
  if (section.empty) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9.5);
    doc.setTextColor(...RGB.inkSoft);
    doc.text(NA + ' — none recorded.', PAGE.marginX, startY + 12);
    return startY + 18;
  }
  autoTable(doc, {
    startY,
    margin: { left: PAGE.marginX, right: PAGE.marginX },
    head: [section.columns],
    body: section.rows.map((r) => r.map((c) => na(c))),
    theme: 'striped',
    headStyles: { fillColor: RGB.brand, textColor: RGB.white, fontStyle: 'bold', fontSize: 8.5, cellPadding: 5 },
    styles: { fontSize: 8.5, cellPadding: 5, textColor: RGB.ink, lineColor: RGB.rule, lineWidth: 0.5, valign: 'top', overflow: 'linebreak' },
    alternateRowStyles: { fillColor: RGB.panelAlt }
  });
  return doc.lastAutoTable.finalY;
}

function pdfCoverBand(doc, model) {
  const W = doc.internal.pageSize.getWidth();
  const h = 96;
  doc.setFillColor(...RGB.brand);
  doc.rect(0, 0, W, h, 'F');
  doc.setTextColor(...RGB.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('PRIVACY INCIDENT — CASE FILE', PAGE.marginX, 30);
  doc.setFontSize(20);
  const titleLines = doc.splitTextToSize(model.title, W - PAGE.marginX * 2);
  doc.text(titleLines[0], PAGE.marginX, 56);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const meta = `Status: ${model.status}${model.id ? '     Case ID: ' + model.id : ''}`;
  doc.text(meta, PAGE.marginX, 80);
  return h + 18;
}

export function exportFullCaseToPdf(incident) {
  const model = buildCaseModel(incident);
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });

  let y = pdfCoverBand(doc, model);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...RGB.inkSoft);
  doc.text(`Generated ${model.generatedAt}`, PAGE.marginX, y);
  y += 14;

  const sections = [
    ['Case overview', () => pdfKeyValueTable(doc, model.overview, y)],
    ['Risk assessment', () => pdfKeyValueTable(doc, model.riskAssessment.rows, y)],
    ['Mitigation actions (accepted)', () => pdfRecordTable(doc, model.mitigations, y)],
    ['Notifications', () => pdfRecordTable(doc, model.notifications, y)],
    ['Sanctions', () => pdfRecordTable(doc, model.sanctions, y)],
    ['Education & training', () => pdfRecordTable(doc, model.education, y)]
  ];

  const pageH = doc.internal.pageSize.getHeight();
  for (const [heading, render] of sections) {
    if (y > pageH - 90) { doc.addPage(); y = PAGE.top; }
    y = pdfHeading(doc, heading, y + 8);
    y = render();
  }

  doc.save(`privacy-case-${model.id || 'incident'}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ===========================================================================
// Risk-assessment-only Word export (kept for the Risk Assessment tab button).
// ===========================================================================
export function exportRiskAssessmentToWord(incident) {
  const model = buildCaseModel(incident);
  const doc = new Document({
    styles: { default: { document: { run: { font: 'Calibri', color: THEME.ink } } } },
    sections: [{
      properties: { page: { margin: { top: 720, bottom: 720, left: 720, right: 720 } } },
      children: [
        wCoverBand({ ...model, title: model.title }),
        new Paragraph({ spacing: { before: 80, after: 200 }, children: [wText(`Generated ${model.generatedAt}`, { italics: true, color: THEME.inkSoft, size: 16 })] }),
        wHeading('Risk assessment'),
        wKeyValueTable(model.riskAssessment.rows)
      ]
    }]
  });
  Packer.toBlob(doc).then((blob) => {
    saveAs(blob, `privacy-risk-assessment-${model.id || 'incident'}-${new Date().toISOString().slice(0, 10)}.docx`);
  });
}
